"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus,
  Search,
  FileText,
  Pin,
  Sparkles,
  Trash2,
  Eye,
  Pencil,
  Split,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  CornerDownRight,
  Link2,
  Move,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  FolderKanban,
  LayoutTemplate,
  ListChecks,
  Home,
  Menu,
  Copy,
} from "lucide-react";
import { useStore } from "@/lib/store";
import type { Doc } from "@/lib/types";
import { AgentHelpButton } from "@/components/ui/AgentHelpButton";
import { CommentsSection } from "@/components/comments/CommentsSection";
import { projectHumanMarkdown, projectReadingMarkdown, replaceCurrentHumanMarkdown } from "@/lib/humanMarkdown";
import { docIdentifier, matchesDocIdentifier } from "@/lib/docIdentity";
import { getDocIssueReferences } from "@/lib/docIssueReferences";
import { Markdown } from "@/components/ui/Markdown";
import { MarkdownEditor } from "@/components/ui/MarkdownEditor";
import { Avatar } from "@/components/ui/Avatar";
import { ZoomControls, useContentZoom, zoomStyle } from "@/components/ui/ContentZoom";
import { ShareMenu } from "@/components/ui/ShareMenu";
import { DocOutline } from "@/components/docs/DocOutline";
import { useDocIssueReferences } from "@/components/docs/useDocIssueReferences";
import { buildOutline } from "@/lib/markdownOutline";
import { formatRelative, cn } from "@/lib/utils";
import { generateDocDraft } from "@/lib/agentDraft";
import {
  getProjectLensSelection,
  restoreLowProminenceCollapseState,
} from "@/components/docs/docsNavigation";
import { TemplatePicker } from "@/components/docs/TemplatePicker";
import { TaskProposalReview } from "@/components/docs/TaskProposalReview";

const FILTER_SELECT_CLS =
  "bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-1.5 py-1 text-[11.5px] text-[var(--fg-secondary)] outline-none focus:border-[var(--accent)] cursor-pointer max-w-[108px] truncate transition-colors";

export function DocsView({
  canWrite,
  onOpenDoc,
  onNavigateHome,
  onOpenNavigation,
}: {
  canWrite: boolean;
  onOpenDoc?: (id: string | null) => void;
  onNavigateHome?: () => void;
  onOpenNavigation?: () => void;
}) {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const currentUserId = useStore((s) => s.currentUserId);
  const workspaces = useStore((s) => s.workspaces);
  const allDocs = useStore((s) => s.docs);
  const allProjects = useStore((s) => s.projects);
  const allTeams = useStore((s) => s.teams);
  const users = useStore((s) => s.users);
  const workspaceMembers = useStore((s) => s.workspaceMembers);
  const selectedDocId = useStore((s) => s.selectedDocId);
  const selectDocInStore = useStore((s) => s.selectDoc);
  const selectDoc = onOpenDoc ?? selectDocInStore;
  const createDoc = useStore((s) => s.createDoc);
  const updateDoc = useStore((s) => s.updateDoc);
  const deleteDoc = useStore((s) => s.deleteDoc);
  const refreshWorkspaceLiveData = useStore((s) => s.refreshWorkspaceLiveData);
  const allIssues = useStore((s) => s.issues);

  const docs = useMemo(
    () => allDocs.filter((d) => d.workspaceId === activeWorkspaceId),
    [allDocs, activeWorkspaceId]
  );
  const projects = useMemo(
    () => allProjects.filter((p) => p.workspaceId === activeWorkspaceId),
    [allProjects, activeWorkspaceId]
  );
  const teams = useMemo(
    () => allTeams.filter((t) => t.workspaceId === activeWorkspaceId),
    [allTeams, activeWorkspaceId]
  );
  const issues = useMemo(
    () => allIssues.filter((i) => i.workspaceId === activeWorkspaceId),
    [allIssues, activeWorkspaceId]
  );
  const workspaceUsers = useMemo(() => {
    const memberIds = new Set(
      workspaceMembers
        .filter((member) => member.workspaceId === activeWorkspaceId)
        .map((member) => member.userId)
    );
    return users.filter((user) => memberIds.has(user.id));
  }, [activeWorkspaceId, users, workspaceMembers]);

  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<"edit" | "preview" | "split">("preview");
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [proposalReviewOpen, setProposalReviewOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<{ docId: string; failed: boolean } | null>(null);
  // Docs navigation filters — scope the tree by project (business unit) / team /
  // tag so a big workspace stays navigable, and optionally group instead of the
  // full tree (Display). In-memory; default is the hierarchical tree.
  const [projectFilter, setProjectFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [groupBy, setGroupBy] = useState<"tree" | "section" | "project" | "team" | "recent">("tree");
  const clearFilters = () => { setProjectFilter(""); setTeamFilter(""); setTagFilter(""); setQuery(""); };
  // Folder collapse state, persisted to localStorage per workspace so closed
  // folders survive page refresh and workspace switches. Key shape matches
  // pauta:sidebarCollapsed / pauta:docsTreeCollapsed conventions. (PAUT-71)
  const collapsedStorageKey = activeWorkspaceId
    ? `pauta:docsFolderCollapsed:${activeWorkspaceId}`
    : null;
  const lowProminenceSeenStorageKey = activeWorkspaceId
    ? `pauta:docsLowProminenceSeen:${activeWorkspaceId}`
    : null;
  const lowProminenceFolderIds = useMemo(
    () =>
      docs
        .filter((doc) => doc.tags.includes("system:low-prominence"))
        .map((doc) => doc.id)
        .sort(),
    [docs]
  );
  const lowProminenceFolderKey = lowProminenceFolderIds.join("|");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !collapsedStorageKey ||
      !lowProminenceSeenStorageKey
    ) {
      return;
    }

    const restored = restoreLowProminenceCollapseState(
      window.localStorage.getItem(collapsedStorageKey),
      window.localStorage.getItem(lowProminenceSeenStorageKey),
      lowProminenceFolderIds
    );

    setCollapsed(restored.collapsed);
    window.localStorage.setItem(
      collapsedStorageKey,
      JSON.stringify(Array.from(restored.collapsed))
    );
    window.localStorage.setItem(
      lowProminenceSeenStorageKey,
      JSON.stringify(Array.from(restored.seen))
    );
  }, [
    collapsedStorageKey,
    lowProminenceSeenStorageKey,
    lowProminenceFolderKey,
    lowProminenceFolderIds,
  ]);
  const [reparentTarget, setReparentTarget] = useState<string | null>(null);
  useEffect(() => {
    if (canWrite) return;
    setMode("preview");
    setReparentTarget(null);
  }, [canWrite]);
  const [isMobile, setIsMobile] = useState(false);
  const { zoom, zoomIn, zoomOut, reset: resetZoom } = useContentZoom();
  // Collapse the docs tree column for distraction-free reading. Persisted so
  // the user's reading-mode preference sticks. Mobile already hides the tree
  // when a doc is selected — this flag only affects desktop.
  const [treeCollapsed, setTreeCollapsed] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined") return;
    setTreeCollapsed(window.localStorage.getItem("pauta:docsTreeCollapsed") !== "0");
  }, []);
  function toggleTreeCollapsed() {
    setTreeCollapsed((v) => {
      const next = !v;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("pauta:docsTreeCollapsed", next ? "1" : "0");
      }
      return next;
    });
  }
  // PAUT-72: independent toggle for the "On this page" outline panel. Lives
  // separately from treeCollapsed so distraction-free reading and the outline
  // can be combined either way.
  const [outlineCollapsed, setOutlineCollapsed] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined") return;
    setOutlineCollapsed(
      window.localStorage.getItem("pauta:docsOutlineCollapsed") !== "0"
    );
  }, []);
  function toggleOutlineCollapsed() {
    setOutlineCollapsed((v) => {
      const next = !v;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("pauta:docsOutlineCollapsed", next ? "1" : "0");
      }
      return next;
    });
  }

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    setIsMobile(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  function docHref(id: string): string {
    if (typeof window === "undefined") return `?doc=${encodeURIComponent(id)}#docs`;
    const url = new URL(window.location.href);
    url.searchParams.delete("issue");
    url.searchParams.set("doc", id);
    url.hash = "docs";
    return url.pathname + url.search + url.hash;
  }

  const workspaceSlug = workspaces.find((workspace) => workspace.id === activeWorkspaceId)?.slug;
  const workspacePath = workspaceSlug
    ? `/${encodeURIComponent(workspaceSlug)}`
    : typeof window !== "undefined" ? new URL(window.location.href).pathname : "/";
  const homeHref = `${workspacePath}#home`;
  const docsHref = `${workspacePath}#docs`;
  function navigateInPlace(event: React.MouseEvent<HTMLAnchorElement>, action?: () => void) {
    if (!action || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    action();
  }

  // Map parentId → children, sorted by position then title
  const childrenByParent = useMemo(() => {
    const map = new Map<string | null, Doc[]>();
    for (const d of docs) {
      const key = d.parentId ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(d);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        if (a.position !== b.position) return a.position - b.position;
        return b.updatedAt - a.updatedAt;
      });
    }
    return map;
  }, [docs]);

  function persistCollapsed(next: Set<string>) {
    if (typeof window === "undefined" || !collapsedStorageKey) return;
    window.localStorage.setItem(collapsedStorageKey, JSON.stringify(Array.from(next)));
  }
  function toggleCollapse(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      persistCollapsed(next);
      return next;
    });
  }

  const allTags = useMemo(
    () => [...new Set(docs.flatMap((d) => d.tags))].sort((a, b) => a.localeCompare(b)),
    [docs]
  );

  const projectDocCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const doc of docs) {
      if (doc.projectId) counts.set(doc.projectId, (counts.get(doc.projectId) ?? 0) + 1);
    }
    return counts;
  }, [docs]);

  function focusProject(projectId: string) {
    if (projectFilter === projectId) {
      setProjectFilter("");
      setGroupBy("tree");
      return;
    }
    selectDoc(getProjectLensSelection(docs, projectId, selectedDocId));
    setQuery("");
    setTeamFilter("");
    setTagFilter("");
    setProjectFilter(projectId);
    setGroupBy("project");
  }

  const humanBodies = useMemo(() => new Map(docs.map((doc) => [doc.id, projectHumanMarkdown(doc.content).body])), [docs]);
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return docs
      .filter(
        (d) =>
          (!q ||
            matchesDocIdentifier(d, query) ||
            d.title.toLowerCase().includes(q) ||
            (humanBodies.get(d.id) ?? "").toLowerCase().includes(q) ||
            d.tags.some((t) => t.toLowerCase().includes(q))) &&
          (!projectFilter || d.projectId === projectFilter) &&
          (!teamFilter || d.teamId === teamFilter) &&
          (!tagFilter || d.tags.includes(tagFilter))
      )
      .sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        return b.updatedAt - a.updatedAt;
      });
  }, [docs, humanBodies, query, projectFilter, teamFilter, tagFilter]);

  const anyFilter = !!(query.trim() || projectFilter || teamFilter || tagFilter);

  // docId → its top-level ancestor folder ("section") — the de-facto grouping
  // today, since docs are organized by the folder tree.
  const docById = useMemo(() => new Map(docs.map((d) => [d.id, d])), [docs]);
  const rootOf = useMemo(() => {
    const cache = new Map<string, Doc>();
    for (const d of docs) {
      let cur = d;
      const seen = new Set<string>();
      while (cur.parentId && docById.has(cur.parentId) && !seen.has(cur.id)) {
        seen.add(cur.id);
        cur = docById.get(cur.parentId)!;
      }
      cache.set(d.id, cur);
    }
    return cache;
  }, [docs, docById]);

  // Display = grouped (by section / project / team): bucket the filtered docs.
  const grouped = useMemo(() => {
    if (groupBy !== "section" && groupBy !== "project" && groupBy !== "team") return null;
    const map = new Map<string, Doc[]>();
    const keyOf = (d: Doc): string =>
      groupBy === "project" ? (d.projectId ?? "__none")
      : groupBy === "team" ? (d.teamId ?? "__none")
      : (rootOf.get(d.id)?.id ?? d.id);
    for (const d of filtered) {
      const k = keyOf(d);
      const arr = map.get(k) ?? [];
      arr.push(d);
      map.set(k, arr);
    }
    const meta = (k: string): { name: string; icon: string } => {
      if (k === "__none") return { name: groupBy === "project" ? "No project" : "No team", icon: "—" };
      if (groupBy === "section") {
        const r = docById.get(k);
        return { name: r?.title ?? "—", icon: r?.icon ?? "📁" };
      }
      const src = groupBy === "project" ? projects.find((p) => p.id === k) : teams.find((t) => t.id === k);
      return { name: src?.name ?? "—", icon: (src?.icon as string | undefined) ?? "•" };
    };
    return [...map.entries()]
      .map(([key, ds]) => ({ key, ...meta(key), docs: ds }))
      .sort((a, b) => (a.key === "__none" ? 1 : b.key === "__none" ? -1 : a.name.localeCompare(b.name)));
  }, [filtered, groupBy, projects, teams, rootOf, docById]);

  const selected = selectedDocId
    ? docs.find((d) => d.id === selectedDocId) || null
    : null;

  // PAUT-71: TOC outline parsed from the selected doc's markdown source. Same
  // slugify + dedupe order as <Markdown>'s heading renderer, so click → id
  // lookups always hit the right element.
  const outline = useMemo(
    () => (selected ? buildOutline(projectReadingMarkdown(selected.content, selected.title).body) : []),
    [selected?.id, selected?.content, selected?.title]
  );
  // The preview pane has its own overflow-y-auto. IntersectionObserver needs
  // that element as its root, otherwise it observes against the viewport and
  // never fires.
  const previewScrollRef = useRef<HTMLDivElement | null>(null);
  // Force DocOutline to re-mount when the ref's element identity changes
  // (mount/unmount of the preview pane) so its effect re-runs with the right
  // root.
  const [previewScrollEl, setPreviewScrollEl] = useState<HTMLDivElement | null>(null);
  const setPreviewScroll = (el: HTMLDivElement | null) => {
    previewScrollRef.current = el;
    setPreviewScrollEl(el);
  };
  useEffect(() => {
    previewScrollRef.current?.scrollTo?.({ top: 0 });
  }, [selected?.id]);
  const selectedAuthor = users.find((u) => u.id === selected?.authorId);
  const selectedProject = projects.find((p) => p.id === selected?.projectId);
  const selectedTeam = teams.find((t) => t.id === selected?.teamId);
  const selectedIdentifier = docIdentifier(selected);
  const referenceRead = useDocIssueReferences({
    doc: selected,
    accessibleIssues: issues,
    workspaceSlug: workspaceSlug ?? "",
    canonicalOrigin: typeof window !== "undefined" ? new URL(window.location.href).origin : undefined,
    currentUserId: currentUserId ?? "",
  });
  const referenceIssueInventory = useMemo(() => {
    const byId = new Map(referenceRead.issues.map((issue) => [issue.id, issue]));
    for (const issue of issues) byId.set(issue.id, issue);
    return [...byId.values()];
  }, [referenceRead.issues, issues]);
  const referencedIssues = useMemo(() => selected ? getDocIssueReferences({
    doc: selected,
    issues: referenceIssueInventory,
    workspaceSlug: workspaceSlug ?? "",
    canonicalOrigin: typeof window !== "undefined" ? new URL(window.location.href).origin : undefined,
    teamPrefixes: teams.map((team) => team.key),
  }) : [], [selected, referenceIssueInventory, workspaceSlug, teams]);
  const referenceFeedback = referenceRead.status === "loading" ? (
    <p role="status" className="text-[12px] text-[var(--fg-tertiary)]">Carregando referências dos tickets…</p>
  ) : referenceRead.status === "error" ? (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <p role="alert" className="text-[12px] text-[var(--fg-tertiary)]">Não foi possível carregar todas as referências.</p>
      <button type="button" onClick={referenceRead.retry}
        className="min-h-11 rounded-md border border-[var(--border)] px-3 text-[12px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]">Tentar novamente</button>
    </div>
  ) : referencedIssues.length === 0 ? (
    <p className="text-[12px] text-[var(--fg-tertiary)]">Nenhum ticket referenciado disponível nesta leitura.</p>
  ) : null;

  function copySelectedIdentifier() {
    if (!selected || !selectedIdentifier) return;
    if (!navigator.clipboard?.writeText) {
      setCopyStatus({ docId: selected.id, failed: true });
      return;
    }
    void navigator.clipboard.writeText(selectedIdentifier)
      .then(() => setCopyStatus({ docId: selected.id, failed: false }))
      .catch(() => setCopyStatus({ docId: selected.id, failed: true }));
  }

  function handleCreate(
    parentId: string | null = null,
    kind: "doc" | "folder" = "doc"
  ) {
    if (!canWrite) return;
    // Folder = a parent-only doc with an empty body; agent + UI both
    // treat it as a grouping node. Same row in `docs`, just an
    // intentional shape that lives at the icon + content layer.
    const isFolder = kind === "folder";
    const doc = createDoc({
      title: isFolder ? "New folder" : "Untitled",
      content: isFolder ? "" : "# Untitled\n\nStart writing…",
      icon: isFolder ? "📁" : "📄",
      parentId,
    });
    selectDoc(doc.id);
    setMode("split");
    if (parentId) {
      // Make sure the new child is visible
      setCollapsed((prev) => {
        const next = new Set(prev);
        next.delete(parentId);
        persistCollapsed(next);
        return next;
      });
    }
  }

  async function handleCreateFromTemplate(input: {
    templateId: string;
    title: string;
    projectId: string | null;
    teamId: string | null;
  }) {
    if (!canWrite || !activeWorkspaceId) return;
    const response = await fetch(
      `/api/workspaces/${encodeURIComponent(activeWorkspaceId)}/templates`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          template_id: input.templateId,
          title: input.title,
          project_id: input.projectId,
          team_id: input.teamId,
        }),
      }
    );
    const payload = (await response.json()) as {
      doc?: { id: string };
      error?: string;
    };
    if (!response.ok || !payload.doc) {
      throw new Error(
        payload.error === "invalid_workspace_reference"
          ? "That project or team is no longer available."
          : "Could not create this doc. Please try again."
      );
    }
    await refreshWorkspaceLiveData(activeWorkspaceId);
    selectDoc(payload.doc.id);
    setMode("split");
  }

  // Ancestor chain for breadcrumbs
  const breadcrumbs = useMemo(() => {
    if (!selected) return [] as Doc[];
    const chain: Doc[] = [];
    let cur: Doc | undefined = selected;
    const seen = new Set<string>();
    while (cur && cur.parentId && !seen.has(cur.id)) {
      seen.add(cur.id);
      const parent = docs.find((d) => d.id === cur!.parentId);
      if (!parent) break;
      chain.unshift(parent);
      cur = parent;
    }
    return chain;
  }, [selected, docs]);

  // Backlinks: other docs whose content contains [[<selected title>]]
  const backlinks = useMemo(() => {
    if (!selected) return [] as Doc[];
    const needle = selected.title.toLowerCase();
    const re = /\[\[([^\]]+?)\]\]/g;
    return docs.filter((d) => {
      if (d.id === selected.id) return false;
      let m: RegExpExecArray | null;
      re.lastIndex = 0;
      while ((m = re.exec(humanBodies.get(d.id) ?? "")) !== null) {
        if (m[1].trim().toLowerCase() === needle) return true;
      }
      return false;
    });
  }, [selected, docs, humanBodies]);

  // Other explicit text references. Ticket references use the shared parser.
  const allCycles = useStore((s) => s.cycles);
  const linked = useMemo(() => {
    if (!selected) return { projects: [], cycles: [], docs: [], tags: [] as string[] };
    const body = humanBodies.get(selected.id) ?? "";
    const projIds = new Set<string>();
    const cycIds = new Set<string>();
    const docIds = new Set<string>();
    const tagSet = new Set<string>();
    let m: RegExpExecArray | null;
    const re2 = /(^|[\s(])@project:([^\s,.;!?\])]+(?:\s+[^\s,.;!?\])]+)*)/g;
    while ((m = re2.exec(body)) !== null) {
      const name = m![2].trim().toLowerCase();
      const p = projects.find((x) => x.name.toLowerCase() === name);
      if (p) projIds.add(p.id);
    }
    const re3 = /(^|[\s(])@cycle:([^\s,.;!?\])]+(?:\s+[^\s,.;!?\])]+)*)/g;
    while ((m = re3.exec(body)) !== null) {
      const name = m![2].trim().toLowerCase();
      const c = allCycles.find((x) => teams.some((team) => team.id === x.teamId) && x.name.toLowerCase() === name);
      if (c) cycIds.add(c.id);
    }
    const re4 = /\[\[([^\]]+?)\]\]/g;
    while ((m = re4.exec(body)) !== null) {
      const t = m![1].trim().toLowerCase();
      const d = docs.find(
        (x) => x.workspaceId === activeWorkspaceId && x.title.toLowerCase() === t
      );
      if (d && d.id !== selected.id) docIds.add(d.id);
    }
    const re5 = /(^|[\s(])#([a-zA-Z][a-zA-Z0-9_-]{1,40})\b/g;
    while ((m = re5.exec(body)) !== null) tagSet.add(m![2]);
    return {
      projects: projects.filter((p) => projIds.has(p.id)),
      cycles: allCycles.filter((c) => cycIds.has(c.id)),
      docs: docs.filter((d) => docIds.has(d.id)),
      tags: Array.from(tagSet),
    };
  }, [selected, docs, humanBodies, projects, teams, allCycles, activeWorkspaceId]);

  const selectIssue = useStore((s) => s.selectIssue);
  const setActiveTag = useStore((s) => s.setActiveTag);

  function handleGenerateDraft() {
    if (!selected || !canWrite) return;
    const relatedIssues = issues.filter(
      (i) =>
        (selected.projectId && i.projectId === selected.projectId) ||
        (selected.teamId && i.teamId === selected.teamId)
    );
    const draft = generateDocDraft({
      title: selected.title,
      projectName: selectedProject?.name,
      teamName: selectedTeam?.name,
      issues: relatedIssues,
    });
    updateDoc(selected.id, { content: draft });
  }

  return (
    <div className="flex-1 flex min-h-0 min-w-0 overflow-hidden">
      {/* Left panel: docs list — full width on mobile when nothing selected,
          hidden on mobile once a doc is open; 300px column on desktop, or 0
          when collapsed via the chevron in the doc header. */}
      <div
        className={cn(
          "min-w-0 flex-col bg-[var(--bg-secondary)]",
          "md:shrink-0 md:flex",
          // Desktop width animates between 300px and 0 (collapsed). Using
          // `w-0 overflow-hidden` instead of `hidden` so the column animates
          // and so the inner Tailwind `md:flex` cleanly overrides the mobile
          // `hidden` set by `selected`.
          treeCollapsed && selected
            ? "md:w-0 md:overflow-hidden md:border-r-0"
            : "md:w-[300px] md:border-r md:border-[var(--border)]",
          "md:transition-[width] md:duration-200 md:ease-out",
          // Mobile: full-width when no doc selected, hidden when a doc is open.
          "border-r border-[var(--border)]",
          selected ? "hidden" : "flex flex-1"
        )}
      >
        <div className="px-3 pt-3 pb-2 flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search
              size={12}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--fg-tertiary)]"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search docs…"
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md pl-7 pr-2 py-1.5 text-[12px] outline-none focus:border-[var(--accent)]"
            />
          </div>
          <AgentHelpButton kind="docs" className="shrink-0 [&>button]:min-h-11 [&>button]:min-w-11 md:[&>button]:min-h-0 md:[&>button]:min-w-0 [&>button]:justify-center" />
          {canWrite && (
            <>
              <button
                onClick={() => setTemplatePickerOpen(true)}
                className="flex shrink-0 h-11 w-11 md:h-7 md:w-7 items-center justify-center rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--fg-tertiary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
                title="Create from template"
                aria-label="Create from template"
              >
                <LayoutTemplate size={13} />
              </button>
              <NewDocButton onCreate={(kind) => handleCreate(null, kind)} />
            </>
          )}
          {selected && <button
            onClick={toggleTreeCollapsed}
            className="hidden md:flex w-7 h-7 rounded-md hover:bg-[var(--bg-hover)] items-center justify-center text-[var(--fg-tertiary)] hover:text-[var(--fg)] transition-colors"
            title="Hide docs tree"
          >
            <PanelLeftClose size={13} />
          </button>}
        </div>
        {projects.length > 0 && projects.length <= 6 && (
          <div className="px-3 pb-2">
            <div className="mb-1 flex items-center gap-1.5 px-0.5 text-[10.5px] uppercase tracking-wider text-[var(--fg-muted)]">
              <FolderKanban size={11} />
              <span className="flex-1">Project lens</span>
              {projectFilter && (
                <button
                  onClick={() => {
                    setProjectFilter("");
                    setGroupBy("tree");
                  }}
                  className="text-[10.5px] normal-case tracking-normal text-[var(--fg-tertiary)] hover:text-[var(--fg)] transition-colors"
                >
                  Show all
                </button>
              )}
            </div>
            <div className="space-y-1">
              {projects
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((project) => {
                  const active = projectFilter === project.id;
                  return (
                    <button
                      key={project.id}
                      onClick={() => focusProject(project.id)}
                      className={cn(
                        "group flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left transition-colors",
                        active
                          ? "border-[var(--accent)] bg-[var(--accent-bg)]"
                          : "border-[var(--border)] bg-[var(--bg-tertiary)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)]"
                      )}
                    >
                      <span className="text-[14px] leading-none">{project.icon}</span>
                      <span className="min-w-0 flex-1 truncate text-[11.5px] font-medium text-[var(--fg-secondary)] group-hover:text-[var(--fg)]">
                        {project.name}
                      </span>
                      <span className="text-[10.5px] tabular-nums text-[var(--fg-muted)]">
                        {projectDocCounts.get(project.id) ?? 0}
                      </span>
                    </button>
                  );
                })}
            </div>
          </div>
        )}
        {/* Filter bar: scope by project (business unit) / team / tag, and pick
            how to display. Solves "big workspace, can't find anything". */}
        <div className="px-3 pb-2 flex items-center gap-1.5 flex-wrap">
          {projects.length > 6 && (
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              title="Project (business unit)"
              className={cn(FILTER_SELECT_CLS, projectFilter && "border-[var(--accent)] text-[var(--fg)]")}
            >
              <option value="">All projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          <select
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            title="Team"
            className={cn(FILTER_SELECT_CLS, teamFilter && "border-[var(--accent)] text-[var(--fg)]")}
          >
            <option value="">All teams</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {allTags.length > 0 && (
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              title="Tag"
              className={cn(FILTER_SELECT_CLS, tagFilter && "border-[var(--accent)] text-[var(--fg)]")}
            >
              <option value="">All tags</option>
              {allTags.map((t) => (
                <option key={t} value={t}>#{t}</option>
              ))}
            </select>
          )}
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as typeof groupBy)}
            title="Display"
            className={cn(FILTER_SELECT_CLS, groupBy !== "tree" && "border-[var(--accent)] text-[var(--fg)]")}
          >
            <option value="tree">Tree</option>
            <option value="section">By section</option>
            <option value="project">By project</option>
            <option value="team">By team</option>
            <option value="recent">Recent</option>
          </select>
          {anyFilter && (
            <button
              onClick={clearFilters}
              className="text-[11px] text-[var(--fg-tertiary)] hover:text-[var(--fg)] px-1 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin px-1 py-1">
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-[11.5px] text-[var(--fg-muted)]">
              {anyFilter ? "No docs match these filters." : "No docs yet."}
            </div>
          )}
          {/* Display = by project/team → grouped sections; any filter / recent
              → flat list; otherwise the recursive tree. */}
          {grouped ? (
            grouped.map((g) => (
              <div key={g.key} className="mb-1.5">
                <div className="px-2 pt-1.5 pb-0.5 flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[var(--fg-muted)]">
                  <span className="text-[12px]">{g.icon}</span>
                  <span className="truncate">{g.name}</span>
                  <span>· {g.docs.length}</span>
                </div>
                {g.docs.map((d) => (
                  <DocListItem
                    key={d.id}
                    doc={d}
                    active={selected?.id === d.id}
                    href={docHref(d.id)}
                    onSelect={() => selectDoc(d.id)}
                    projectName={projects.find((p) => p.id === d.projectId)?.name}
                    teamName={teams.find((t) => t.id === d.teamId)?.name}
                  />
                ))}
              </div>
            ))
          ) : anyFilter || groupBy === "recent" ? (
            filtered.map((d) => (
              <DocListItem
                key={d.id}
                doc={d}
                active={selected?.id === d.id}
                href={docHref(d.id)}
                onSelect={() => selectDoc(d.id)}
                projectName={projects.find((p) => p.id === d.projectId)?.name}
                teamName={teams.find((t) => t.id === d.teamId)?.name}
              />
            ))
          ) : (
            <DocTreeNodes
              parentId={null}
              depth={0}
              childrenByParent={childrenByParent}
              selectedId={selected?.id ?? null}
              collapsed={collapsed}
              onSelect={selectDoc}
              onToggle={toggleCollapse}
              onAddChild={handleCreate}
              onReparent={(id) => setReparentTarget(id)}
              canWrite={canWrite}
              hrefForId={docHref}
            />
          )}
        </div>
      </div>

      {/* Only the navigation stays above the reading scroll surface. */}
      {selected ? (
        <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
          <nav aria-label="Navegação do documento" className="flex min-h-11 shrink-0 min-w-0 items-center gap-1 border-b border-[var(--border)] px-2 sm:px-4">
            {onOpenNavigation && (
              <button type="button" onClick={onOpenNavigation} aria-label="Abrir menu do workspace"
                className="md:hidden flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]">
                <Menu size={17} />
              </button>
            )}
            <a href={homeHref} onClick={(event) => navigateInPlace(event, onNavigateHome)}
              className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-md px-2 text-[12.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]">
              <Home size={15} /> Home
            </a>
            <a href={docsHref} onClick={(event) => navigateInPlace(event, () => selectDoc(null))}
              className="inline-flex min-h-11 min-w-0 items-center gap-1.5 rounded-md px-2 text-[12.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]">
              <ChevronLeft size={15} className="shrink-0" /> Voltar a Docs
            </a>
            <button type="button" onClick={toggleTreeCollapsed}
              aria-label={treeCollapsed ? "Mostrar árvore de Docs" : "Ocultar árvore de Docs"}
              aria-pressed={!treeCollapsed}
              className="hidden md:flex h-11 w-11 shrink-0 ml-auto items-center justify-center rounded-md text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]">
              {treeCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
          </nav>
          <div className="flex-1 flex min-h-0 min-w-0">
            <div ref={setPreviewScroll} className="flex-1 min-w-0 overflow-y-auto overscroll-contain scrollbar-thin">
              <div className={cn("mx-auto w-full min-w-0 px-4 py-5 sm:px-6 md:px-8 md:py-8", mode === "split" && canWrite ? "max-w-[1100px]" : "max-w-[55rem]")}>
                <header className="mb-6 min-w-0">
                  <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2 text-[12px] text-[var(--fg-tertiary)]">
                    <span aria-hidden="true" className="text-[18px]">{selected.icon}</span>
                    {selectedIdentifier && <button type="button" onClick={copySelectedIdentifier} aria-label={"Copiar " + selectedIdentifier}
                      className="inline-flex min-h-11 items-center gap-1.5 rounded-md px-2 font-mono text-[14px] font-semibold text-[var(--accent)] hover:bg-[var(--accent-bg)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]">
                      <span>{selectedIdentifier}</span><Copy size={13} aria-hidden="true" />
                    </button>}
                    {copyStatus?.docId === selected.id && <span role="status" className="text-[12px] text-[var(--fg-tertiary)]">
                      {copyStatus.failed ? "Não foi possível copiar o identificador." : "Identificador copiado."}
                    </span>}
                  </div>
                  {canWrite && mode === "edit" ? (
                    <input value={selected.title} onChange={(event) => updateDoc(selected.id, { title: event.target.value })}
                      aria-label="Document title"
                      className="min-h-11 w-full min-w-0 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 text-[20px] font-semibold outline-none focus:border-[var(--accent)]" />
                  ) : (
                    <h1 id="doc-reading-title" className="text-[25px] sm:text-[30px] font-semibold leading-[1.2] tracking-tight text-[var(--fg)] [overflow-wrap:anywhere]">
                      {selected.title}
                    </h1>
                  )}
                  <details key={selected.id} className="mt-4 min-w-0 rounded-lg border border-[var(--border)]">
                    <summary className="min-h-11 cursor-pointer rounded-lg px-3 py-3 text-[12px] font-medium text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]">
                      Detalhes e ações
                    </summary>
                    <div className="min-w-0 space-y-3 border-t border-[var(--border)] p-3">
                      {breadcrumbs.length > 0 && (
                        <nav aria-label="Hierarquia do documento" className="flex min-w-0 flex-wrap items-center gap-1">
                          {breadcrumbs.map((parent) => (
                            <a key={parent.id} href={docHref(parent.id)} onClick={(event) => navigateInPlace(event, () => selectDoc(parent.id))}
                              className="inline-flex min-h-11 max-w-full items-center gap-1 rounded-md px-2 text-[12px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] [overflow-wrap:anywhere]">
                              <span aria-hidden="true">{parent.icon}</span>{parent.title}<ChevronRight size={12} className="shrink-0" />
                            </a>
                          ))}
                        </nav>
                      )}
                      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 text-[12px] text-[var(--fg-tertiary)] [overflow-wrap:anywhere]">
                        {selectedAuthor && <span className="inline-flex items-center gap-1.5"><Avatar user={selectedAuthor} size={16} />{selectedAuthor.name}</span>}
                        <span>Atualizado {formatRelative(selected.updatedAt)}</span>
                        {selectedProject && <span>{selectedProject.icon} {selectedProject.name}</span>}
                        {selectedTeam && !selectedProject && <span>{selectedTeam.name}</span>}
                        {selected.tags.map((tag) => <span key={tag} className="rounded-full border border-[var(--border)] px-2 py-1">#{tag}</span>)}
                      </div>
                      <section aria-label="Tickets referenciados nos detalhes" className="min-w-0 border-t border-[var(--border)] pt-3">
                        <h2 className="mb-1 text-[12px] font-semibold text-[var(--fg-secondary)]">Tickets referenciados{(referenceRead.status === "ready" || referencedIssues.length > 0) && <span> ({referencedIssues.length} disponíveis)</span>}</h2>
                        {referenceFeedback}
                        {referencedIssues.length > 0 && (
                          <div className="space-y-1">
                            {referencedIssues.map(({ issue }) => (
                              <a key={issue.id} href={workspacePath + "?issue=" + encodeURIComponent(issue.id)}
                                onClick={(event) => navigateInPlace(event, () => selectIssue(issue.id))}
                                className="flex min-h-11 min-w-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-md px-2 py-2 text-[12px] hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]">
                                <span className="font-mono text-[var(--accent)]">{issue.identifier}</span>
                                <span className="min-w-0 text-[var(--fg-secondary)] [overflow-wrap:anywhere]">{issue.title}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </section>
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <div className="[&_button]:min-h-11 [&_button]:min-w-11">
                          <ZoomControls zoom={zoom} zoomIn={zoomIn} zoomOut={zoomOut} reset={resetZoom} />
                        </div>
                        {outline.length >= 2 && (
                          <button type="button" onClick={toggleOutlineCollapsed} aria-pressed={!outlineCollapsed}
                            className="hidden lg:inline-flex min-h-11 items-center gap-2 rounded-md px-3 text-[12px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]">
                            {outlineCollapsed ? <PanelRightOpen size={15} /> : <PanelRightClose size={15} />}
                            {outlineCollapsed ? "Mostrar sumário" : "Ocultar sumário"}
                          </button>
                        )}
                        {canWrite && (
                          <>
                            <div className="[&_button]:min-h-11 [&_button]:min-w-11">
                              <ShareMenu key={selected.id} type="doc" id={selected.id} identifier={selectedIdentifier} isPublic={selected.isPublic} />
                            </div>
                            <div role="group" aria-label="Modo do documento" className="flex overflow-hidden rounded-md border border-[var(--border)]">
                              <ModeBtn active={mode === "preview"} onClick={() => setMode("preview")} title="Ler" icon={<Eye size={15} />} />
                              <ModeBtn active={mode === "edit"} onClick={() => setMode("edit")} title="Editar" icon={<Pencil size={15} />} />
                              <ModeBtn active={mode === "split"} onClick={() => setMode("split")} title="Dividir" icon={<Split size={15} />} />
                            </div>
                            <DocAction icon={<CornerDownRight size={15} />} label="Criar subdocumento" onClick={() => handleCreate(selected.id)} />
                            <DocAction icon={<Pin size={15} />} label={selected.pinned ? "Desafixar" : "Fixar"} active={selected.pinned}
                              onClick={() => updateDoc(selected.id, { pinned: !selected.pinned })} />
                            <DocAction icon={<Sparkles size={15} />} label="Gerar rascunho com IA" onClick={handleGenerateDraft} />
                            <DocAction icon={<ListChecks size={15} />} label="Propor tarefas" onClick={() => setProposalReviewOpen(true)} />
                            <DocAction icon={<Trash2 size={15} />} label="Excluir documento" danger
                              onClick={() => { if (confirm("Delete this doc?")) deleteDoc(selected.id); }} />
                          </>
                        )}
                      </div>
                    </div>
                  </details>
                </header>
                <div className={cn("min-w-0", canWrite && mode === "split" && "grid gap-6 md:grid-cols-2")}>
                  {canWrite && (mode === "edit" || mode === "split") && (
                    <section aria-label="Editar fonte Markdown" className="min-w-0">
                      <div className="h-[65dvh] min-h-72 overflow-hidden rounded-lg border border-[var(--border)]">
                        <MarkdownEditor value={selected.content} onChange={(value) => updateDoc(selected.id, { content: value })}
                          placeholder="Start writing… type / for commands. Paste or drop images to upload." className="h-full" />
                      </div>
                    </section>
                  )}
                  {(!canWrite || mode === "preview" || mode === "split") && (
                    <article aria-labelledby="doc-reading-title" className="min-w-0 [overflow-wrap:anywhere]" style={zoomStyle(zoom)}>
                      <Markdown content={selected.content} readingTitle={selected.title} referencedIssues={referenceIssueInventory}
                        onImageAnnotated={canWrite ? (oldUrl, newUrl) => updateDoc(selected.id, {
                          content: replaceCurrentHumanMarkdown(selected.content, oldUrl, newUrl),
                        }) : undefined} />
                      <section aria-label="Tickets referenciados" className="mt-8 border-t border-[var(--border)] pt-4">
                        <h2 className="mb-2 text-[13px] font-semibold text-[var(--fg-secondary)]">Tickets referenciados</h2>
                        {referenceFeedback}
                        {referencedIssues.length > 0 && (
                          <div className="space-y-1">
                            {referencedIssues.map(({ issue }) => (
                              <a key={issue.id} href={workspacePath + "?issue=" + encodeURIComponent(issue.id)}
                                onClick={(event) => navigateInPlace(event, () => selectIssue(issue.id))}
                                className="flex min-h-11 min-w-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-md px-2 py-2 text-[12.5px] hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]">
                                <span className="font-mono text-[var(--accent)]">{issue.identifier}</span>
                                <span className="min-w-0 text-[var(--fg-secondary)] [overflow-wrap:anywhere]">{issue.title}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </section>
                      {(childrenByParent.get(selected.id)?.length ?? 0) > 0 && (
                        <section className="mt-8 border-t border-[var(--border)] pt-4">
                          <h2 className="mb-2 text-[13px] font-semibold text-[var(--fg-secondary)]">Subdocumentos</h2>
                          {childrenByParent.get(selected.id)!.map((child) => (
                            <a key={child.id} href={docHref(child.id)} onClick={(event) => navigateInPlace(event, () => selectDoc(child.id))}
                              className="flex min-h-11 min-w-0 items-start gap-2 rounded-md px-2 py-3 text-[12.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] [overflow-wrap:anywhere]">
                              <span aria-hidden="true">{child.icon}</span>{child.title}
                            </a>
                          ))}
                        </section>
                      )}
                      {(linked.projects.length > 0 || linked.cycles.length > 0 || linked.tags.length > 0) && (
                        <details className="mt-6 rounded-lg border border-[var(--border)]">
                          <summary className="min-h-11 cursor-pointer px-3 py-3 text-[12px] text-[var(--fg-secondary)]">Outras referências no documento</summary>
                          <div className="flex flex-wrap gap-2 border-t border-[var(--border)] p-3">
                            {linked.projects.map((project) => <span key={project.id} className="rounded-md bg-[var(--bg-tertiary)] px-2 py-2 text-[12px]">{project.icon} {project.name}</span>)}
                            {linked.cycles.map((cycle) => <span key={cycle.id} className="rounded-md bg-[var(--bg-tertiary)] px-2 py-2 text-[12px]">↻ {cycle.name}</span>)}
                            {linked.tags.map((tag) => <button key={tag} onClick={() => setActiveTag(tag)}
                              className="min-h-11 rounded-md border border-[var(--border)] px-3 text-[12px] hover:bg-[var(--bg-hover)]">#{tag}</button>)}
                          </div>
                        </details>
                      )}
                      <CommentsSection parentType="doc" parentId={selected.id} comments={selected.comments ?? []} canWrite={canWrite} />
                      {backlinks.length > 0 && (
                        <details className="mt-6 rounded-lg border border-[var(--border)]">
                          <summary className="min-h-11 cursor-pointer px-3 py-3 text-[12px] text-[var(--fg-secondary)]">Referenciado por outros documentos ({backlinks.length})</summary>
                          <div className="border-t border-[var(--border)] p-2">
                            {backlinks.map((backlink) => (
                              <a key={backlink.id} href={docHref(backlink.id)} onClick={(event) => navigateInPlace(event, () => selectDoc(backlink.id))}
                                className="flex min-h-11 min-w-0 items-start gap-2 rounded-md px-2 py-3 text-[12.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] [overflow-wrap:anywhere]">
                                <Link2 size={14} className="mt-0.5 shrink-0" />{backlink.title}
                              </a>
                            ))}
                          </div>
                        </details>
                      )}
                    </article>
                  )}
                </div>
              </div>
            </div>
            {!outlineCollapsed && !isMobile && mode !== "edit" && previewScrollEl && (
              <DocOutline outline={outline} scrollRoot={previewScrollEl} />
            )}
          </div>
        </div>
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center relative">
          <div className="text-center max-w-sm px-6">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--bg-tertiary)] border border-[var(--border)] flex items-center justify-center">
              <FileText size={20} className="text-[var(--fg-tertiary)]" />
            </div>
            <h3 className="text-[15px] font-semibold mb-1">Knowledge lives here</h3>
            <p className="text-[13px] text-[var(--fg-tertiary)] mb-4">
              Create runbooks, specs, research memos, and onboarding docs. Export the
              whole workspace as an Obsidian vault.
            </p>
            {canWrite && (
              <button
                onClick={() => handleCreate()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[12.5px] font-medium"
              >
                <Plus size={13} />
                New doc
              </button>
            )}
          </div>
        </div>
      )}

      {canWrite && reparentTarget && (
        <ReparentModal
          docs={docs}
          target={docs.find((d) => d.id === reparentTarget)!}
          onPick={(parentId) => {
            updateDoc(reparentTarget, { parentId });
            setReparentTarget(null);
          }}
          onClose={() => setReparentTarget(null)}
        />
      )}
      {canWrite && activeWorkspaceId && templatePickerOpen && (
        <TemplatePicker
          workspaceId={activeWorkspaceId}
          projects={projects}
          teams={teams}
          onCreate={handleCreateFromTemplate}
          onClose={() => setTemplatePickerOpen(false)}
        />
      )}
      {canWrite && activeWorkspaceId && selected && proposalReviewOpen && (
        <TaskProposalReview
          workspaceId={activeWorkspaceId}
          docId={selected.id}
          docTitle={selected.title}
          teams={teams}
          projects={projects}
          users={workspaceUsers}
          onClose={() => setProposalReviewOpen(false)}
        />
      )}
    </div>
  );
}

function ReparentModal({
  docs,
  target,
  onPick,
  onClose,
}: {
  docs: Doc[];
  target: Doc;
  onPick: (parentId: string | null) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  // Build the set of descendants of target (can't pick those — would cycle)
  const descendantIds = useMemo(() => {
    const set = new Set<string>([target.id]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const d of docs) {
        if (d.parentId && set.has(d.parentId) && !set.has(d.id)) {
          set.add(d.id);
          changed = true;
        }
      }
    }
    return set;
  }, [docs, target.id]);

  const candidates = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return docs
      .filter((d) => !descendantIds.has(d.id))
      .filter((d) => !ql || d.title.toLowerCase().includes(ql))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [docs, descendantIds, q]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-[15vh]"
      onClick={onClose}
    >
      <div
        className="w-[480px] max-w-[90vw] rounded-lg border border-[var(--border-strong)] bg-[var(--surface-elevated)] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-2">
          <Move size={13} className="text-[var(--accent)]" />
          <h4 className="text-[12.5px] font-semibold flex-1">
            Move <span className="text-[var(--fg-secondary)]">{target.icon} {target.title}</span> under…
          </h4>
        </div>
        <div className="p-3 border-b border-[var(--border)]">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a parent doc…"
            className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)]"
          />
        </div>
        <div className="max-h-[50vh] overflow-y-auto scrollbar-thin py-1">
          <button
            onClick={() => onPick(null)}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[var(--bg-hover)] text-[12.5px] text-left"
          >
            <CornerDownRight size={11} className="text-[var(--fg-tertiary)]" />
            <span className="text-[var(--fg-tertiary)] italic">Top level (no parent)</span>
          </button>
          {candidates.length === 0 ? (
            <div className="px-3 py-4 text-[12px] text-[var(--fg-muted)] text-center">
              No matches.
            </div>
          ) : (
            candidates.map((d) => (
              <button
                key={d.id}
                onClick={() => onPick(d.id)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[var(--bg-hover)] text-[12.5px] text-left"
              >
                <span className="w-4 text-center">{d.icon}</span>
                <span className="flex-1 truncate">{d.title}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function DocListItem({
  doc,
  active,
  onSelect,
  href,
  projectName,
  teamName,
}: {
  doc: Doc;
  active: boolean;
  onSelect: () => void;
  href?: string;
  projectName?: string;
  teamName?: string;
}) {
  return (
    <motion.a
      href={href ?? "#"}
      onClick={(e) => {
        // Let modifier-clicks (Cmd/Ctrl/Shift) and middle-clicks fall
        // through to the browser → opens in new tab/window.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        onSelect();
      }}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.12 }}
      className={cn(
        "w-full min-h-11 md:min-h-0 flex items-start gap-2 px-2 py-2 rounded-md text-left transition-colors no-underline text-inherit",
        active ? "bg-[var(--bg-active)]" : "hover:bg-[var(--bg-hover)]"
      )}
    >
      <span className="text-[15px] shrink-0 mt-0.5">{doc.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {docIdentifier(doc) && <span className="shrink-0 font-mono text-[11.5px] font-semibold text-[var(--accent)]">{docIdentifier(doc)}</span>}
          <span className="text-[12.5px] font-medium truncate">{doc.title}</span>
          {doc.pinned && <Pin size={10} className="text-[var(--accent)] shrink-0" />}
        </div>
        <div className="text-[11px] text-[var(--fg-muted)] truncate mt-0.5">
          {projectName || teamName || "General"} · {formatRelative(doc.updatedAt)}
        </div>
      </div>
    </motion.a>
  );
}

function NewDocButton({
  onCreate,
  compact = false,
  title = "New doc or folder",
}: {
  onCreate: (kind: "doc" | "folder") => void;
  compact?: boolean;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function pick(kind: "doc" | "folder") {
    setOpen(false);
    onCreate(kind);
  }

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen((v) => !v);
        }}
        className={cn(
          "rounded-md flex items-center justify-center transition-colors",
          compact
            ? "opacity-0 group-hover:opacity-100 w-5 h-5 text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
            : "w-11 h-11 md:w-7 md:h-7 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
        )}
        title={title}
      >
        <Plus size={compact ? 11 : 14} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.12 }}
            // For compact (per-row) anchor under button so it doesn't
            // get clipped by row overflow. Top variant anchors right of
            // the parent flex.
            className={cn(
              "absolute z-20 w-[180px] rounded-md border border-[var(--border-strong)] bg-[var(--surface-elevated)] shadow-lg overflow-hidden",
              compact ? "left-0 top-6" : "right-0 top-9"
            )}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                pick("doc");
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12.5px] hover:bg-[var(--bg-hover)] transition-colors"
            >
              <span className="text-[14px]">📄</span>
              <div>
                <div className="font-medium">Doc</div>
                <div className="text-[10.5px] text-[var(--fg-muted)]">Markdown content</div>
              </div>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                pick("folder");
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12.5px] hover:bg-[var(--bg-hover)] transition-colors border-t border-[var(--border)]"
            >
              <span className="text-[14px]">📁</span>
              <div>
                <div className="font-medium">Folder</div>
                <div className="text-[10.5px] text-[var(--fg-muted)]">Group sub-docs</div>
              </div>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DocAction({
  icon,
  label,
  onClick,
  active = false,
  danger = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex min-h-11 max-w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[12px] font-medium transition-colors hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]",
        active && "bg-[var(--bg-active)] text-[var(--fg)]",
        danger
          ? "text-[var(--red)]"
          : "text-[var(--fg-secondary)]"
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function ModeBtn({
  active,
  onClick,
  icon,
  title,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex min-h-11 items-center gap-1.5 px-3 text-[12px] border-l border-[var(--border)] first:border-l-0 transition-colors focus-visible:outline-2 focus-visible:outline-[var(--accent)]",
        active
          ? "bg-[var(--bg-active)] text-[var(--fg)]"
          : "text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
      )}
      title={title}
      aria-pressed={active}
      type="button"
    >
      {icon}{title}
    </button>
  );
}

function DocTreeNodes({
  parentId,
  depth,
  childrenByParent,
  selectedId,
  collapsed,
  onSelect,
  onToggle,
  onAddChild,
  onReparent,
  canWrite,
  hrefForId,
}: {
  parentId: string | null;
  depth: number;
  childrenByParent: Map<string | null, Doc[]>;
  selectedId: string | null;
  collapsed: Set<string>;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onAddChild: (id: string, kind: "doc" | "folder") => void;
  onReparent: (id: string) => void;
  canWrite: boolean;
  hrefForId?: (id: string) => string;
}) {
  const nodes = childrenByParent.get(parentId) ?? [];
  if (nodes.length === 0) return null;
  return (
    <>
      {nodes.map((d) => {
        const hasChildren = (childrenByParent.get(d.id)?.length ?? 0) > 0;
        const isCollapsed = collapsed.has(d.id);
        const active = selectedId === d.id;
        return (
          <div key={d.id}>
            <a
              href={hrefForId ? hrefForId(d.id) : "#"}
              className={cn(
                "group flex min-h-11 md:min-h-0 items-center gap-0.5 pr-1 rounded-md cursor-pointer no-underline text-inherit",
                active ? "bg-[var(--bg-active)]" : "hover:bg-[var(--bg-hover)]"
              )}
              style={{ paddingLeft: 4 + depth * 12 }}
              onClick={(e) => {
                // Modifier-clicks (Cmd/Ctrl/Shift) and middle-clicks
                // fall through to the browser → new tab/window. Plain
                // click is handled in-app.
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                e.preventDefault();
                onSelect(d.id);
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  // preventDefault is critical here: the wrapping <a href> would
                  // otherwise navigate (Next does a full client nav on ?doc=X),
                  // which remounts the component and resets the in-memory
                  // `collapsed` Set — the folder visually closes then springs
                  // back open after the loading flash.
                  e.preventDefault();
                  e.stopPropagation();
                  if (hasChildren) onToggle(d.id);
                }}
                className={cn(
                  "w-11 h-11 md:w-4 md:h-6 flex items-center justify-center shrink-0 text-[var(--fg-tertiary)]",
                  !hasChildren && "invisible"
                )}
              >
                {isCollapsed ? <ChevronRight size={11} /> : <ChevronDown size={11} />}
              </button>
              <span className="shrink-0 w-4 text-center text-[13px]">{d.icon}</span>
              {docIdentifier(d) && <span className="shrink-0 font-mono text-[11.5px] font-semibold text-[var(--accent)]">{docIdentifier(d)}</span>}
              <span className="flex-1 min-w-0 truncate text-[12.5px] text-[var(--fg-secondary)] py-1">
                {d.title}
                {d.pinned && (
                  <Pin size={9} className="inline-block ml-1 text-[var(--accent)]" />
                )}
              </span>
              {canWrite && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onReparent(d.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 w-5 h-5 rounded flex items-center justify-center text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
                    title="Move under…"
                  >
                    <Move size={10} />
                  </button>
                  <NewDocButton
                    compact
                    title="Add sub-doc or sub-folder"
                    onCreate={(kind) => onAddChild(d.id, kind)}
                  />
                </>
              )}
            </a>
            {!isCollapsed && hasChildren && (
              <DocTreeNodes
                parentId={d.id}
                depth={depth + 1}
                childrenByParent={childrenByParent}
                selectedId={selectedId}
                collapsed={collapsed}
                onSelect={onSelect}
                onToggle={onToggle}
                onAddChild={onAddChild}
                onReparent={onReparent}
                canWrite={canWrite}
                hrefForId={hrefForId}
              />
            )}
          </div>
        );
      })}
    </>
  );
}
